import { useCallback } from 'react';
import type { ThinkSpace, ThinkSpaceExport, CanvasElement } from '@/types/thinkspace';
import { thinkspacesService } from '@/services/collections/thinkspaces';

const EXPORT_VERSION = '1.0';

export type ExportFormat = 'tspace' | 'png' | 'svg';

// Helper to escape XML special characters
function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export const useImportExport = () => {
  // Export as ThinkSpace format (.tspace - JSON)
  const exportAsThinkSpace = useCallback((thinkSpace: ThinkSpace) => {
    const exportData: ThinkSpaceExport = {
      version: EXPORT_VERSION,
      exportedAt: new Date().toISOString(),
      thinkSpace: {
        name: thinkSpace.name,
        description: thinkSpace.description,
        canvasSize: thinkSpace.canvasSize,
        backgroundColor: thinkSpace.backgroundColor,
        elements: thinkSpace.elements.map((el) => {
          // Remove Firestore-specific fields
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { createdAt, updatedAt, createdBy, ...rest } = el as CanvasElement & { createdAt?: unknown; updatedAt?: unknown; createdBy?: string };
          return rest;
        }),
      },
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${thinkSpace.name.replace(/[^a-z0-9]/gi, '_')}.tspace`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  // Export as PNG
  const exportAsPNG = useCallback(async (thinkSpace: ThinkSpace, canvasElement: HTMLElement) => {
    try {
      // Use html2canvas to capture the canvas
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(canvasElement, {
        backgroundColor: thinkSpace.backgroundColor,
        scale: 2, // Higher quality
        useCORS: true,
        logging: false,
      });

      // Convert to PNG and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${thinkSpace.name.replace(/[^a-z0-9]/gi, '_')}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }, 'image/png');
    } catch (err) {
      console.error('Failed to export as PNG:', err);
      throw err;
    }
  }, []);

  // Export as SVG
  const exportAsSVG = useCallback((thinkSpace: ThinkSpace) => {
    const { canvasSize, backgroundColor, elements } = thinkSpace;

    // Build SVG content
    let svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="${canvasSize.width}" height="${canvasSize.height}" viewBox="0 0 ${canvasSize.width} ${canvasSize.height}">
  <rect width="100%" height="100%" fill="${backgroundColor}" />
`;

    // Sort elements by zIndex
    const sortedElements = [...elements].sort((a, b) => a.zIndex - b.zIndex);

    for (const el of sortedElements) {
      switch (el.type) {
        case 'postit': {
          const colors: Record<string, string> = {
            yellow: '#FFF59D',
            pink: '#F8BBD9',
            blue: '#90CAF9',
            green: '#A5D6A7',
            orange: '#FFCC80',
            purple: '#CE93D8',
          };
          const bgColor = colors[el.color] || colors.yellow;
          svgContent += `  <rect x="${el.position.x}" y="${el.position.y}" width="${el.size.width}" height="${el.size.height}" fill="${bgColor}" rx="8" />
  <text x="${el.position.x + 12}" y="${el.position.y + 24}" font-family="Space Grotesk, sans-serif" font-size="14" font-weight="600" fill="#151414">${escapeXml(el.title)}</text>
  <text x="${el.position.x + 12}" y="${el.position.y + 48}" font-family="Space Grotesk, sans-serif" font-size="12" fill="#151414">${escapeXml(el.content.substring(0, 50))}</text>
`;
          break;
        }
        case 'text': {
          svgContent += `  <text x="${el.position.x}" y="${el.position.y + el.fontSize}" font-family="${el.fontFamily}, sans-serif" font-size="${el.fontSize}" fill="${el.color}"${el.bold ? ' font-weight="bold"' : ''}${el.italic ? ' font-style="italic"' : ''}>${escapeXml(el.content)}</text>
`;
          break;
        }
        case 'line': {
          svgContent += `  <line x1="${el.position.x}" y1="${el.position.y}" x2="${el.endPosition.x}" y2="${el.endPosition.y}" stroke="${el.strokeColor}" stroke-width="${el.strokeWidth}" />
`;
          break;
        }
        case 'box': {
          svgContent += `  <rect x="${el.position.x}" y="${el.position.y}" width="${el.size.width}" height="${el.size.height}" fill="${el.fillColor || 'none'}" stroke="${el.strokeColor}" stroke-width="${el.strokeWidth}" rx="${el.borderRadius}" />
`;
          break;
        }
        case 'ellipse': {
          const cx = el.position.x + el.size.width / 2;
          const cy = el.position.y + el.size.height / 2;
          const rx = el.size.width / 2;
          const ry = el.size.height / 2;
          svgContent += `  <ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="${el.fillColor || 'none'}" stroke="${el.strokeColor}" stroke-width="${el.strokeWidth}" />
`;
          break;
        }
        case 'diamond': {
          const w = el.size.width;
          const h = el.size.height;
          const x = el.position.x;
          const y = el.position.y;
          const points = `${x + w / 2},${y} ${x + w},${y + h / 2} ${x + w / 2},${y + h} ${x},${y + h / 2}`;
          svgContent += `  <polygon points="${points}" fill="${el.fillColor || 'none'}" stroke="${el.strokeColor}" stroke-width="${el.strokeWidth}" />
`;
          break;
        }
        case 'connector': {
          let pathD = `M ${el.startPosition.x} ${el.startPosition.y}`;
          for (const pt of el.routingPoints) {
            pathD += ` L ${pt.x} ${pt.y}`;
          }
          pathD += ` L ${el.endPosition.x} ${el.endPosition.y}`;
          svgContent += `  <path d="${pathD}" fill="none" stroke="${el.strokeColor}" stroke-width="${el.strokeWidth}" stroke-linecap="round" stroke-linejoin="round" />
`;
          break;
        }
        case 'image': {
          svgContent += `  <image x="${el.position.x}" y="${el.position.y}" width="${el.size.width}" height="${el.size.height}" href="${el.imageUrl}" opacity="${el.opacity}" />
`;
          break;
        }
      }
    }

    svgContent += '</svg>';

    // Download
    const blob = new Blob([svgContent], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${thinkSpace.name.replace(/[^a-z0-9]/gi, '_')}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  // Legacy export (calls exportAsThinkSpace)
  const exportThinkSpace = exportAsThinkSpace;

  const importThinkSpace = useCallback(
    async (
      file: File,
      thinkSpaceId: string,
      userId: string,
      mode: 'replace' | 'merge' = 'replace'
    ): Promise<void> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = async (e) => {
          try {
            const content = e.target?.result as string;
            const importData: ThinkSpaceExport = JSON.parse(content);

            // Validate version
            if (!importData.version || !importData.thinkSpace) {
              throw new Error('Invalid export file format');
            }

            // Get current thinkspace
            const currentSpace = await thinkspacesService.getById(thinkSpaceId);
            if (!currentSpace) {
              throw new Error('ThinkSpace not found');
            }

            // Restore timestamps and creator
            const now = new Date();
            const restoredElements = importData.thinkSpace.elements.map(
              (el) => ({
                ...el,
                id: mode === 'replace' ? el.id : crypto.randomUUID(),
                createdAt: { toDate: () => now } as unknown as CanvasElement['createdAt'],
                updatedAt: { toDate: () => now } as unknown as CanvasElement['updatedAt'],
                createdBy: userId,
              } as CanvasElement)
            );

            let newElements: CanvasElement[];
            if (mode === 'replace') {
              newElements = restoredElements;
            } else {
              // Merge: adjust positions to not overlap
              const offset = { x: 50, y: 50 };
              const offsetElements = restoredElements.map((el) => ({
                ...el,
                position: {
                  x: el.position.x + offset.x,
                  y: el.position.y + offset.y,
                },
              }));
              newElements = [...currentSpace.elements, ...offsetElements];
            }

            // Update thinkspace
            await thinkspacesService.updateElements(thinkSpaceId, newElements);

            // Optionally update canvas size and background
            if (mode === 'replace') {
              await thinkspacesService.update(thinkSpaceId, {
                canvasSize: importData.thinkSpace.canvasSize,
                backgroundColor: importData.thinkSpace.backgroundColor,
              });
            }

            resolve();
          } catch (err) {
            reject(err);
          }
        };

        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
      });
    },
    []
  );

  return {
    exportThinkSpace,
    exportAsThinkSpace,
    exportAsPNG,
    exportAsSVG,
    importThinkSpace,
  };
};
